import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ResultRow,
  ResultItem,
} from 'components/components/SearchAutocompleteResults/SearchAutocompleteResults.types';

import {
  fetchDefaultSuggestions,
  fetchSuggestionsByQuery,
  fetchHistoryByEmail,
  clearHistoryByEmail,
  initAutocomplete,
  fetchSuggestionsItems,
  deleteHistoryItem,
} from '../../api/autocomplete';
import { fetchQuickFilters } from '../../api/quickFilters';
import {
  getDefaultSuggestions,
  getSuggestions,
  getSuggestedItems,
  setQuickFilterSS,
  getQuickFilterSS,
} from '../../helpers';
import { LocalHistoryItem, RecommendationItem } from '../../types';

export interface QuickFilter {
  groupName: string;
  label: string;
  type: string;
  suggesterContext: string[];
  value: string;
  field: string;
}
interface AutocompleteState {
  query: string;
  shadowQuery?: string;
  history: LocalHistoryItem[];
  suggestions?: ResultRow;
  defaultSuggestions?: ResultRow;
  isLoading: boolean;
  error?: string;
  isOpen: boolean;
  items: RecommendationItem[];
  itemsQuery?: string;
  isSuggestionsRequested?: boolean;
  currentItemIndex?: number;
  currentProductIndex?: number;
  xRequestId?: string;
  hoveredItem?: ResultItem;
  quickFilters?: QuickFilter[];
  currentQuickFilter?: QuickFilter;
}

const getQuery = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams?.get('q') || '';
};

const initialState: AutocompleteState = {
  history: [],
  isLoading: false,
  suggestions: undefined,
  items: [],
  itemsQuery: undefined,
  query: getQuery(),
  shadowQuery: undefined,
  isOpen: false,
  isSuggestionsRequested: false,
  currentItemIndex: undefined,
  currentProductIndex: undefined,
  xRequestId: undefined,
  hoveredItem: undefined,
  quickFilters: [],
  currentQuickFilter: getQuickFilterSS(),
};

const autocompleteSlice = createSlice({
  name: 'autocomplete',
  initialState,
  reducers: {
    setQuery(state, { payload }: PayloadAction<string>) {
      state.query = payload;
    },
    setShadowQuery(state, { payload }: PayloadAction<string | undefined>) {
      state.shadowQuery = payload;
    },
    setIsOpen(state, { payload }: PayloadAction<boolean>) {
      state.isOpen = payload;
    },
    setIsSuggestionsRequested(state, { payload }: PayloadAction<boolean>) {
      state.isSuggestionsRequested = payload;
    },
    setCurrentItemIndex(state, { payload }: PayloadAction<number | undefined>) {
      state.currentItemIndex = payload;
    },
    setCurrentProductIndex(
      state,
      { payload }: PayloadAction<number | undefined>,
    ) {
      state.currentProductIndex = payload;
    },
    setXRequestId(state, { payload }) {
      state.xRequestId = payload;
    },
    setHoveredItem(state, { payload }) {
      state.hoveredItem = payload;
    },
    setCurrentQuickFilter(state, { payload }) {
      state.currentQuickFilter = payload;
      setQuickFilterSS(payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initAutocomplete.pending, (state) => {
      if (
        state.defaultSuggestions ||
        state.history.length > 0 ||
        state.suggestions
      ) {
        return;
      }
      state.isLoading = true;
    });
    builder.addCase(initAutocomplete.fulfilled, (state, { payload }) => {
      const newHistoryItems = payload?.history?.entries
        ? payload.history.entries.map((item) => ({
            value: item.query,
            id: `${item.query}_history`,
            suggesterType: item?.suggesterType,
            suggesterValue: item?.suggesterValue,
            type: 'history',
          }))
        : undefined;

      const newSuggestions = getSuggestions(payload?.suggestions);
      const newDefaultSuggestions = getDefaultSuggestions(
        payload.defaultSuggestions,
      );
      if (payload.items) {
        state.items = payload.items;
      }
      if (newDefaultSuggestions) {
        state.defaultSuggestions = newDefaultSuggestions;
      }
      if (state.query?.length > 0 && state.query?.length < 3) {
        state.isSuggestionsRequested = false;
      }
      if (payload.itemsQuery) {
        state.itemsQuery = payload.itemsQuery;
      }
      if (newHistoryItems) {
        state.history = newHistoryItems;
      } else {
        state.suggestions = newSuggestions ? newSuggestions : undefined;
      }
      state.isLoading = false;
    });
    builder.addCase(initAutocomplete.rejected, (state) => {
      state.isLoading = false;
      state.error = 'Failed to fetch autocomplete items';
    });

    builder.addCase(fetchDefaultSuggestions.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchDefaultSuggestions.fulfilled, (state, { payload }) => {
      const suggestions = getDefaultSuggestions(payload);
      state.suggestions = suggestions ? suggestions : undefined;
      state.isLoading = false;
    });
    builder.addCase(fetchDefaultSuggestions.rejected, (state) => {
      state.isLoading = false;
      state.error = 'Failed to fetch default suggestions';
    });

    builder.addCase(fetchHistoryByEmail.pending, (state) => {});
    builder.addCase(fetchHistoryByEmail.fulfilled, (state, { payload }) => {
      if (payload?.entries == null) {
        state.history = [];
        return;
      }
      const newHistoryItems = payload.entries.map((item) => ({
        value: item.query,
        id: `${item.query}_history`,
        type: 'history',
      }));
      state.history = newHistoryItems;
    });
    builder.addCase(fetchHistoryByEmail.rejected, (state) => {
      state.error = 'Failed to fetch history';
    });

    builder.addCase(fetchSuggestionsByQuery.pending, (state) => {
      if (Array.isArray(state.suggestions) && state.suggestions.length === 0) {
        state.isLoading = true;
      }
    });

    builder.addCase(fetchSuggestionsByQuery.fulfilled, (state, { payload }) => {
      const newSuggestions = getSuggestions(payload.data);
      const suggestedItems = getSuggestedItems(payload.data);
      state.items = suggestedItems;
      state.xRequestId = payload.xRequestId;
      state.suggestions = newSuggestions ? newSuggestions : undefined;
      state.isLoading = false;
      state.isSuggestionsRequested = true;
      if (payload.data?.itemsQuery) {
        state.itemsQuery = payload.data?.itemsQuery;
      }
    });
    builder.addCase(fetchSuggestionsByQuery.rejected, (state) => {
      state.suggestions = undefined;
      state.isLoading = false;
      state.isSuggestionsRequested = true;
      state.error = 'Failed to fetch suggestions';
    });

    builder.addCase(clearHistoryByEmail.pending, (state) => {
      state.history = [];
      state.isOpen = false;
    });

    builder.addCase(fetchSuggestionsItems.pending, (state) => {});
    builder.addCase(fetchSuggestionsItems.fulfilled, (state, { payload }) => {
      const suggestedItems = getSuggestedItems(payload.data);
      state.xRequestId = payload.xRequestId;
      state.items = suggestedItems;
      if (payload.data?.itemsQuery) {
        state.itemsQuery = payload.data?.itemsQuery;
      }
    });
    builder.addCase(fetchSuggestionsItems.rejected, (state) => {
      state.isLoading = false;
      state.error = 'Failed to fetch autocomplete items';
    });
    builder.addCase(deleteHistoryItem.fulfilled, (state, { payload }) => {
      if (payload.success) {
        const newHistory = [...state.history].filter(
          (item) => item.value !== payload.value,
        );
        state.history = newHistory;
      }
    });
    builder.addCase(fetchQuickFilters.fulfilled, (state, { payload }) => {
      if (Array.isArray(payload)) {
        state.quickFilters = payload;
        if (state.currentQuickFilter == null) {
          state.currentQuickFilter = payload?.[0];
        }
      } else {
        state.quickFilters = [];
        state.currentQuickFilter = undefined;
      }
    });
    builder.addCase(fetchQuickFilters.rejected, (state) => {
      state.quickFilters = [];
      state.error = 'Failed to fetch quick filters';
    });
  },
});

export const {
  setQuery,
  setShadowQuery,
  setIsOpen,
  setIsSuggestionsRequested,
  setCurrentItemIndex,
  setHoveredItem,
  setXRequestId,
  setCurrentProductIndex,
  setCurrentQuickFilter,
} = autocompleteSlice.actions;

export default autocompleteSlice.reducer;
